import { Trans } from "@lingui/macro";
import { FC, useState } from "react";
import {
  StyledPromoCode,
  StyledPromoCodeButton,
  StyledPromoCodeWedge,
  StyledPromoCodeWrapper,
  StyledPromoCodeText,
  StyledPromoCodePrompt,
  StyledBannerContent,
  StyledContentWrapper,
  StyledText,
  StyledBannerHeadingNew,
} from "src/molecules/PromotionBanner/PromotionBanner.styled";
import { PromotionBannerProps } from "./PromotionBannerTypes";
import { StyledBannerImg } from "../BrandCover/BrandCover.styled";
import { Box } from "@otrium/core";
import { theme } from "src/theme";
import { CopyIcon } from "@otrium/icons";

interface PromotionBannerTypeThreeProps extends PromotionBannerProps {
  backgroundColor: string;
  buttonColor: string;
  textColor: string;
  promoCode?: string | null;
  promoCodeColor?: string | null;
}

const PromotionBannerTypeThree: FC<PromotionBannerTypeThreeProps> = ({
  bannerImageSrc,
  title,
  description,
  buttonTitle,
  buttonColor,
  buttonTextColor,
  conditionText,
  backgroundColor,
  textColor = "White",
  promoCode,
  promoCodeColor,
  hideCTAButton,
  disableBackgroundColor,
  textAlignment,
  titleColor,
}) => {
  const [copyState, setCopyState] = useState<"pristine" | "copied" | "errored">(
    "pristine"
  );

  const copyPromoCode = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const code = (
      e.currentTarget as HTMLDivElement
    ).querySelector<HTMLSpanElement>(".code")?.textContent;

    try {
      if (code) {
        await navigator.clipboard.writeText(code);
        setCopyState("copied");
      }
    } catch (err) {
      setCopyState("errored");
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <StyledBannerImg src={bannerImageSrc} />
      <StyledContentWrapper textAlignment={textAlignment}>
        <StyledBannerContent
          disableBackgroundColor={disableBackgroundColor}
          backgroundColor={backgroundColor}
          textAlignment={textAlignment}
        >
          <StyledBannerHeadingNew
            as="h1"
            color={titleColor || "tone.white"}
            textAlignment={textAlignment || "center"}
            fontWeight="700"
          >
            {title}
          </StyledBannerHeadingNew>
          {description && (
            <StyledText
              as="p"
              fontSize={"fontSize16"}
              lineHeight={"24px"}
              textAlign="center"
              color={textColor}
              textAlignment={textAlignment || "center"}
            >
              {description}
            </StyledText>
          )}

          {promoCode && !hideCTAButton ? (
            <StyledPromoCodeWrapper
              outlineColor={buttonColor ?? "Black"}
              promoCodeColor={promoCodeColor ?? "Black"}
              textAlignment={textAlignment}
            >
              <StyledPromoCode
                promoCodeColor={promoCodeColor ?? "Black"}
                onClick={(e) => void copyPromoCode(e)}
                onMouseLeave={() => setCopyState("pristine")}
              >
                <CopyIcon className="icon" />
                <StyledPromoCodeText className="code" color={buttonColor}>
                  {promoCode}
                </StyledPromoCodeText>
                <StyledPromoCodePrompt className="prompt">
                  {copyState === "copied" && <Trans>Copied!</Trans>}
                  {copyState === "pristine" && <Trans>Click to copy</Trans>}
                  {copyState === "errored" && <Trans>Copying failed</Trans>}
                </StyledPromoCodePrompt>
              </StyledPromoCode>
              <StyledPromoCodeWedge
                backgroundColor={buttonColor ?? theme.colors.tone.black}
              />
              <StyledPromoCodeButton
                textColor={buttonTextColor || "tone.white"}
                backgroundColor={buttonColor ?? theme.colors.tone.black}
              >
                {buttonTitle}
              </StyledPromoCodeButton>
            </StyledPromoCodeWrapper>
          ) : null}

          {conditionText && (
            <StyledText
              fontSize="fontSize12"
              color={textColor}
              textAlignment={textAlignment || "center"}
            >
              {conditionText}
            </StyledText>
          )}
        </StyledBannerContent>
      </StyledContentWrapper>
    </Box>
  );
};

export default PromotionBannerTypeThree;
